import { useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
const FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
/** Kapsayıcıdaki klavyeyle odaklanabilir öğeler (belge sırasıyla). */
export function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => !el.hasAttribute('inert') && el.closest('[inert]') === null && !el.hidden);
}
/**
 * Katmanı `document.body` altına taşır (taşan/overflow kapsayıcılarda kırpılmaz). Düğüm ilk render'da oluşturulur ve
 * layout aşamasında belgeye eklenir; böylece katman içeriği, sahibinin effect'leri (odak, inert) çalışmadan önce
 * belgededir.
 *
 * `anchorRef` verilirse ve tetikleyici zaten bir portal katmanının (ör. açık Dialog/Sheet) içindeyse düğüm o katmanın
 * kökü altına eklenir: modal açıkken diğer body çocukları `inert` olduğundan, iç menü/açılır liste ancak böyle
 * etkileşimli kalır (WP 2.8, TB-27-2).
 */
export function Portal({ children, live = false, anchorRef, }) {
    const [node] = useState(() => {
        const el = document.createElement('div');
        el.setAttribute('data-cf-portal', '');
        if (live)
            el.setAttribute('data-cf-live', '');
        return el;
    });
    useLayoutEffect(() => {
        // En yakın portal düğümü (henüz belgeye eklenmemiş bir katman içinde de çalışır: `closest` ayrık ağaçta yürür)
        const host = anchorRef?.current?.closest('[data-cf-portal]') ?? document.body;
        host.appendChild(node);
        return () => {
            node.remove();
        };
    }, [node, anchorRef]);
    return createPortal(children, node);
}
const stack = [];
let nextId = 1;
const inertByUs = new Set();
function portalRootOf(node) {
    let cur = node;
    while (cur && cur.parentElement !== document.body)
        cur = cur.parentElement;
    return cur;
}
function applyInert() {
    for (const el of inertByUs)
        el.removeAttribute('inert');
    inertByUs.clear();
    const top = stack.at(-1);
    if (!top) {
        document.body.style.removeProperty('overflow');
        return;
    }
    const keep = portalRootOf(top.node());
    for (const child of Array.from(document.body.children)) {
        if (child === keep || child.tagName === 'SCRIPT')
            continue;
        // Toast bölgesi her zaman duyurulabilir kalır (canlı bölge inert olursa okunmaz)
        if (child.hasAttribute('data-cf-live'))
            continue;
        if (!child.hasAttribute('inert')) {
            child.setAttribute('inert', '');
            inertByUs.add(child);
        }
    }
    document.body.style.overflow = 'hidden';
}
/**
 * Escape en üstteki modal katmanı kapatır — ama katman içindeki bir bileşen (açık liste, menü, seçici) Escape'i zaten
 * işlediyse (`preventDefault`/`stopPropagation`) dokunmaz: dinleyici kabarcık aşamasındadır ve önce iç öğe kapanır.
 */
function onDocumentKeyDown(e) {
    if (e.key !== 'Escape' || e.defaultPrevented)
        return;
    const top = stack.at(-1);
    if (!top)
        return;
    e.stopPropagation();
    top.onEscape();
}
/**
 * Modal katman davranışı (Dialog, Sheet): açıldığında arka plan `inert`, odak içeride tutulur (Tab/Shift+Tab döner),
 * Escape en üstteki katmanı kapatır, kapanınca odak katmanı açan öğeye döner, sayfa kaydırması kilitlenir.
 */
export function useModalLayer(opts) {
    const { open, containerRef, initialFocusRef, returnFocusRef } = opts;
    const onCloseRef = useRef(opts.onClose);
    onCloseRef.current = opts.onClose;
    useEffect(() => {
        if (!open)
            return;
        const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const returnTarget = returnFocusRef?.current ?? previous;
        const layer = {
            id: nextId++,
            node: () => containerRef.current,
            onEscape: () => {
                onCloseRef.current();
            },
        };
        stack.push(layer);
        if (stack.length === 1)
            document.addEventListener('keydown', onDocumentKeyDown);
        applyInert();
        const container = containerRef.current;
        const focusTarget = initialFocusRef?.current ?? (container ? getFocusable(container)[0] : undefined) ?? container;
        focusTarget?.focus();
        function onKeyDown(e) {
            if (e.key !== 'Tab' || stack.at(-1)?.id !== layer.id || !container)
                return;
            const items = getFocusable(container);
            if (items.length === 0) {
                e.preventDefault();
                container.focus();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            if (e.shiftKey && (active === first || active === container)) {
                e.preventDefault();
                last?.focus();
            }
            else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first?.focus();
            }
        }
        container?.addEventListener('keydown', onKeyDown);
        return () => {
            container?.removeEventListener('keydown', onKeyDown);
            const i = stack.findIndex((l) => l.id === layer.id);
            if (i >= 0)
                stack.splice(i, 1);
            if (stack.length === 0)
                document.removeEventListener('keydown', onDocumentKeyDown);
            applyInert();
            if (returnTarget?.isConnected)
                returnTarget.focus();
        };
        // Açık kaldığı sürece bir kez kurulur; ref'ler sabittir
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
}
/**
 * Modal olmayan açılır katman (menü, popover): dışarıya tıklama veya Escape kapatır; Escape'te odak tetikleyiciye döner.
 */
export function useDismissable(opts) {
    const { open, refs } = opts;
    const onDismissRef = useRef(opts.onDismiss);
    onDismissRef.current = opts.onDismiss;
    useEffect(() => {
        if (!open)
            return;
        function onPointerDown(e) {
            const t = e.target;
            if (t && refs.some((r) => r.current?.contains(t)))
                return;
            onDismissRef.current('outside');
        }
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                // Modal katmana ulaşmadan önce yalnız bu açılır katman kapanır
                e.preventDefault();
                e.stopPropagation();
                onDismissRef.current('escape');
            }
        }
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
}
const EDGE = 8;
const GAP = 4;
/**
 * Saf yerleşim hesabı (TB-27-2): tercihen alta açılır; altta yer yoksa ve üstte yer varsa üste döner; iki tarafta da
 * sığmıyorsa daha geniş tarafı seçer ve `maxHeight` ile sınırlar (katman taşmaz, içerik kayar). Yatayda görünüm alanı
 * içinde tutulur. %200 yakınlaştırmada görünüm alanı küçüldüğünden aynı kural katmanı erişilebilir tutar.
 */
export function computeAnchoredPosition(a, layer, viewport, align = 'start') {
    const below = viewport.height - a.bottom - GAP - EDGE;
    const above = a.top - GAP - EDGE;
    let placement = 'bottom';
    if (layer.height > below && (layer.height <= above || above > below))
        placement = 'top';
    const space = Math.max(0, placement === 'bottom' ? below : above);
    const height = Math.min(layer.height, space);
    const top = placement === 'bottom' ? a.bottom + GAP : a.top - GAP - height;
    const width = Math.min(layer.width, viewport.width - 2 * EDGE);
    let left = align === 'end' ? a.right - width : a.left;
    left = Math.max(EDGE, Math.min(left, viewport.width - width - EDGE));
    const anchorHidden = a.bottom < 0 || a.top > viewport.height || a.right < 0 || a.left > viewport.width;
    return {
        top: Math.round(top),
        left: Math.round(left),
        maxHeight: Math.max(0, Math.floor(space)),
        placement,
        anchorWidth: Math.round(a.width),
        anchorHidden,
    };
}
/**
 * Tetikleyiciye göre sabit (fixed) konum: katman portalda olduğundan kaydırma kapsayıcısında kırpılmaz. Kaydırma
 * (tüm kapsayıcılar, yakalama aşaması), pencere boyutu ve katman/tetikleyici boyut değişiminde (ResizeObserver) bir
 * animasyon karesinde yeniden hesaplanır.
 */
export function useAnchoredPosition(open, anchorRef, layerRef, align = 'start') {
    const [pos, setPos] = useState(null);
    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return;
        }
        let frame = 0;
        function place() {
            const a = anchorRef.current?.getBoundingClientRect();
            const l = layerRef.current;
            if (!a || !l)
                return;
            // Ölçüm doğal yükseklikle: önceki maxHeight sınırı geçici kaldırılır
            const prevMax = l.style.maxHeight;
            l.style.maxHeight = '';
            const size = { width: l.offsetWidth, height: l.scrollHeight || l.offsetHeight };
            l.style.maxHeight = prevMax;
            const next = computeAnchoredPosition(a, size, { width: window.innerWidth, height: window.innerHeight }, align);
            setPos((cur) => cur !== null &&
                cur.top === next.top &&
                cur.left === next.left &&
                cur.maxHeight === next.maxHeight &&
                cur.anchorHidden === next.anchorHidden &&
                cur.anchorWidth === next.anchorWidth
                ? cur
                : next);
        }
        function schedule() {
            if (frame !== 0)
                return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                place();
            });
        }
        place();
        window.addEventListener('resize', schedule);
        window.addEventListener('scroll', schedule, true);
        const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
        if (ro) {
            if (anchorRef.current)
                ro.observe(anchorRef.current);
            if (layerRef.current)
                ro.observe(layerRef.current);
        }
        return () => {
            if (frame !== 0)
                window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', schedule);
            window.removeEventListener('scroll', schedule, true);
            ro?.disconnect();
        };
    }, [open, anchorRef, layerRef, align]);
    return pos;
}
/** Konum → satır içi stil. Konum henüz yoksa veya tetikleyici görünmüyorsa katman görünmez yerleştirilir. */
export function anchoredStyle(pos, opts = {}) {
    if (pos === null)
        return { visibility: 'hidden', top: 0, left: 0 };
    return {
        top: pos.top,
        left: pos.left,
        maxHeight: pos.maxHeight,
        overflowY: 'auto',
        ...(opts.matchAnchorWidth === true ? { minWidth: pos.anchorWidth } : {}),
        ...(pos.anchorHidden ? { visibility: 'hidden' } : {}),
    };
}
//# sourceMappingURL=overlay.js.map